import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { logDmbErrorLocation } from '../utils/error';
import { requestAgVizMachineDataMaps } from '../requesters/agVizRequester';
import {
  AgVizAction,
  RequestAgVizMapsAction,
  receiveAgVizMaps,
  agVizMapsFailure,
} from '../actions/agVizActions';
import { DMB_WEB_AG_VIZ } from '../constants/featureFlags';
import { getHarvestArdDataFeatureSelector } from '../selectors/featureSelectors';

/**
 * Get Ag-Viz Machine Data Maps from UAPI
 * @param {RequestAgVizMapsAction} action
 * @return {void}
 */
export function* getAgVizMaps(action: RequestAgVizMapsAction) {
  try {
    const state = yield select();
    const agVizEnabled = state.feature?.userConfigFeatures?.[DMB_WEB_AG_VIZ]?.value;

    if (!agVizEnabled) {
      // Feature flag disabled, exit early without making request
      return;
    }

    const { fieldId, cropCode, seasonName, seasonYear, activityType } = action;

    const cropId = state.crops?.[cropCode]?.['dfp-uuid'];
    
    if (!cropId) {
      logDmbErrorLocation(
        new Error(`Crop ID not found for crop code: ${cropCode}`),
        'agViz saga - getAgVizMaps'
      );
      yield put(agVizMapsFailure(`Crop ID not found for crop code: ${cropCode}`));
      return;
    }

    const harvestArdEnabled = getHarvestArdDataFeatureSelector(state);
    const maps = yield call(requestAgVizMachineDataMaps, {
      fieldId,
      cropId,
      seasonName,
      seasonYear,
      activityType,
      harvestArdEnabled
    });

    yield put(
      receiveAgVizMaps({
        maps,
        cropCode,
        seasonName,
        seasonYear,
        activityType
      })
    );
  } catch (error) {
    logDmbErrorLocation(error, 'agViz saga - getAgVizMaps');
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch Ag-Viz maps';
    yield put(agVizMapsFailure(errorMessage));
  }
}

/**
 * Waits for the RequestAgVizMaps action and calls the getAgVizMaps saga
 * @return {void}
 */
export function* getAgVizMapsWatcher() {
  yield takeEvery(AgVizAction.RequestAgVizMaps, getAgVizMaps);
}

/**
 * Uses fork to start the watcher
 * @return {void}
 */
export default function* rootSaga() {
  yield fork(getAgVizMapsWatcher);
}
